/**
 * The compte rendu: what the machine drafted, what the practitioner made of it, and the signature.
 *
 * The panel is built around one refusal. It never lets a document be signed that nobody has read —
 * the suite is chosen first, because it is what the conclusion is written FOR; the text is then
 * shown as an editable draft rather than a preview; and validating is a separate, deliberate act
 * that names what it does.
 *
 * The three texts stay visibly three. A draft says it is a draft, on screen and on the page it
 * renders to. A signed compte rendu is read-only and carries who signed it, when, with which
 * identifier, and the fingerprint of the exact bytes — a trace nobody can see is a trace nobody
 * checks. Editing again means reopening the dossier, which is a deliberate step and not an
 * accident of clicking in a textarea.
 *
 * Changing the suite discards the draft on purpose: the conclusion was written for the other
 * answer, and silently keeping a paragraph that recommends a consultation under a decision not to
 * offer one is the one failure mode this whole chain exists to prevent.
 */

import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Download,
  FileSignature,
  Loader2,
  PenLine,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import { Textarea } from "@app/components/ui/textarea";
import { cn } from "@app/lib/utils";
import {
  SUITE_LABELS,
  SUITE_ORDER,
  formatDateTime,
  parseReport,
  type CompteRendu,
  type Envoi,
  type SuiteDonnee,
} from "@app/utils/teleexpertise";

interface CompteRenduPanelProps {
  compteRendu: CompteRendu;
  /** The dossier's current suite, when one has already been recorded. */
  suiteDonnee: SuiteDonnee | null;
  statut: "recu" | "avis_rendu";
  busy: string | null;
  onGenerate: (suite: SuiteDonnee) => void;
  onSave: (texte: string) => void;
  onValidate: (suite: SuiteDonnee, texte: string, note: string) => void;
  onDownload: (brouillon: boolean) => void;
  onReopen: () => void;
  note: string;
  onNoteChange: (note: string) => void;
}

export function CompteRenduPanel({
  compteRendu,
  suiteDonnee,
  statut,
  busy,
  onGenerate,
  onSave,
  onValidate,
  onDownload,
  onReopen,
  note,
  onNoteChange,
}: CompteRenduPanelProps) {
  const { i18n, t } = useLingui();
  const avis = compteRendu.avis;
  const signed = statut === "avis_rendu" && avis !== null;

  // Seeded from what the DRAFT was written for before anything else: a reload must come back to
  // the conclusion on screen, not to an empty choice the practitioner can silently answer the other
  // way.
  const [suite, setSuite] = useState<SuiteDonnee | null>(
    compteRendu.suite_redigee ?? suiteDonnee ?? avis?.suite_donnee ?? null,
  );
  const [texte, setTexte] = useState(compteRendu.texte_travail ?? "");
  // The last text the server acknowledged. Compared against rather than a dirty flag, so a save
  // that failed leaves the panel saying « non enregistré » instead of quietly claiming success.
  const saved = useRef(compteRendu.texte_travail ?? "");

  useEffect(() => {
    setTexte(compteRendu.texte_travail ?? "");
    saved.current = compteRendu.texte_travail ?? "";
  }, [compteRendu.texte_travail]);

  const dirty = texte !== saved.current;
  const hasDraft = Boolean(texte.trim());
  // Changing the outcome does not rewrite the conclusion, so it must not be signable until it has
  // been rewritten. Said on screen rather than refused at the last click.
  const stale = hasDraft && compteRendu.suite_redigee != null && suite !== compteRendu.suite_redigee;

  if (signed && avis) {
    return (
      <section className="rounded-xl border border-emerald-200 border-l-4 border-l-emerald-500 bg-emerald-50/40 shadow-md">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-200 px-4 py-3">
          <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
            <ShieldCheck className="h-4 w-4 text-emerald-700" />
            <Trans>Compte rendu validé</Trans>
          </h2>
          <Button
            type="button"
            size="sm"
            onClick={() => onDownload(false)}
            className="bg-primary hover:bg-primary/90"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            <Trans>Télécharger le PDF</Trans>
          </Button>
        </header>

        <div className="space-y-1 border-b border-emerald-200 px-4 py-3 text-xs text-slate-600">
          <p>
            <Trans>
              Validé par <span className="font-semibold text-slate-900">{avis.praticien}</span> le{" "}
              {formatDateTime(avis.valide_le, i18n.locale) ?? "?"}
            </Trans>
          </p>
          {(avis.praticien_rpps || avis.praticien_adeli) && (
            <p>
              {avis.praticien_rpps && <Trans>RPPS {avis.praticien_rpps}</Trans>}
              {avis.praticien_rpps && avis.praticien_adeli && " · "}
              {avis.praticien_adeli && <Trans>ADELI {avis.praticien_adeli}</Trans>}
            </p>
          )}
          <p>{i18n._(SUITE_LABELS[avis.suite_donnee])}</p>
          <p className="text-slate-500">
            {avis.texte_modifie ? (
              <Trans>Rédigé par Mage-X, corrigé par le praticien.</Trans>
            ) : (
              <Trans>Rédigé par Mage-X, validé sans modification.</Trans>
            )}
          </p>
          {/* Shown, not hidden: the fingerprint is what a transport will key off the day one
              exists, and a trace nobody can see is a trace nobody checks. */}
          <p className="font-mono text-[10px] text-slate-400" title={avis.empreinte}>
            <Trans>Empreinte du document : {avis.empreinte.slice(0, 16)}…</Trans>
          </p>
        </div>

        <ReportText texte={avis.texte} />

        <footer className="border-t border-emerald-200 px-4 py-3">
          <Envois envois={compteRendu.envois} />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={onReopen}
            className="mt-2 border-slate-300 text-slate-700 hover:border-primary/50 hover:text-primary"
          >
            <PenLine className="mr-1.5 h-3.5 w-3.5" />
            <Trans>Rouvrir pour corriger</Trans>
          </Button>
        </footer>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 border-l-4 border-l-primary bg-white shadow-md">
      <header className="border-b border-slate-100 px-4 py-3">
        <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
          <FileSignature className="h-4 w-4 text-primary" />
          <Trans>Compte rendu</Trans>
        </h2>
        <p className="mt-0.5 text-xs text-slate-600">
          <Trans>
            Mage-X rédige à partir des réponses du questionnaire. Relisez, corrigez, puis validez
            — c'est la validation qui rend l'avis.
          </Trans>
        </p>
      </header>

      {/* The suite comes first because the conclusion is written FOR it, not labelled with it. */}
      <div className="space-y-2 border-b border-slate-100 px-4 py-3">
        <span className="text-xs font-medium text-slate-700">
          <Trans>Suite donnée</Trans>
        </span>
        <div className="flex flex-wrap gap-2">
          {SUITE_ORDER.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setSuite(value)}
              aria-pressed={suite === value}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                // Outlined when chosen, like every other toggle in the app. Filled, it was a solid
                // blue sitting beside the solid blue of « Rédiger » — a state and an action wearing
                // the same colour, which is the one thing a primary is supposed to reserve.
                suite === value
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-slate-300 bg-white text-slate-600 hover:border-primary/50 hover:text-primary",
              )}
            >
              {i18n._(SUITE_LABELS[value])}
            </button>
          ))}
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!suite || busy !== null}
          onClick={() => suite && onGenerate(suite)}
          className="bg-primary hover:bg-primary/90"
          title={!suite ? t`Indiquez d'abord la suite donnée` : undefined}
        >
          {busy === "generate" ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : hasDraft ? (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          )}
          {hasDraft ? <Trans>Rédiger à nouveau</Trans> : <Trans>Rédiger le compte rendu</Trans>}
        </Button>
        {hasDraft && !stale && (
          <p className="text-[11px] text-slate-500">
            <Trans>Rédiger à nouveau remplace le texte en cours, corrections comprises.</Trans>
          </p>
        )}
        {stale && (
          <p className="rounded-md border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-[11px] font-medium text-slate-700">
            <Trans>
              Le compte rendu affiché conclut sur l'autre suite. Relancez la rédaction avant de
              valider.
            </Trans>
          </p>
        )}
      </div>

      {!hasDraft ? (
        <p className="px-4 py-6 text-center text-sm text-slate-500">
          <Trans>Choisissez la suite donnée, puis lancez la rédaction.</Trans>
        </p>
      ) : (
        <div className="space-y-3 px-4 py-3">
          <Textarea
            value={texte}
            onChange={(event) => setTexte(event.target.value)}
            onBlur={() => dirty && onSave(texte)}
            rows={18}
            className="bg-white text-[13px] leading-relaxed"
            aria-label={t`Texte du compte rendu`}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
            <span>
              {compteRendu.genere_le && (
                <Trans>
                  Rédigé le {formatDateTime(compteRendu.genere_le, i18n.locale) ?? "?"}
                  {compteRendu.modele ? ` · ${compteRendu.modele}` : ""}
                </Trans>
              )}
            </span>
            <span className={cn(dirty && "font-medium text-slate-700")}>
              {dirty ? (
                <Trans>Modifications non enregistrées</Trans>
              ) : (
                <Trans>Brouillon enregistré</Trans>
              )}
            </span>
          </div>

          <Textarea
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder={t`Note interne pour les autres praticiens (ne figure pas sur le compte rendu)`}
            className="bg-white text-sm"
            rows={2}
            maxLength={2000}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!suite || busy !== null || !texte.trim() || stale}
              onClick={() => suite && onValidate(suite, texte, note)}
              title={stale ? t`Le texte a été rédigé pour l'autre suite — relancez la rédaction` : undefined}
              className="bg-primary hover:bg-primary/90"
            >
              {busy === "validate" ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              <Trans>Valider et signer</Trans>
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy !== null || dirty}
              onClick={() => onDownload(true)}
              title={dirty ? t`Enregistrez vos corrections d'abord` : undefined}
              className="border-slate-300 text-slate-700 hover:border-primary/50 hover:text-primary"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              <Trans>Aperçu PDF</Trans>
            </Button>
            {dirty && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-slate-600"
                onClick={() => onSave(texte)}
              >
                <Trans>Enregistrer le brouillon</Trans>
              </Button>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            <Trans>
              Valider fige ce texte, produit le PDF et enregistre qui l'a signé et quand. Le
              dossier passe à « Rendu ».
            </Trans>
          </p>
        </div>
      )}
    </section>
  );
}

/** A signed compte rendu, read the way the PDF lays it out. */
/**
 * What became of each copy of the signed compte rendu.
 *
 * It exists because « parti » and « pas parti » look identical on a screen that says nothing, and
 * only one of them is expensive: a surgeon who assumes a patient received their compte rendu does
 * not chase it. So a copy that did not leave says so, in red, with an icon and the reason — and one
 * that did stays deliberately quiet, in slate.
 *
 * No green on a copy that left. Green and red are a human's verdict on a value in this app — the
 * practitioner validating the document above is what earns the green frame around it — and a mail
 * provider accepting a message is a machine state. Red is spent here on the other rule it carries:
 * failure. The address is shown because a compte rendu sent to the wrong one is the failure that
 * reports itself as a success.
 */
function Envois({ envois }: { envois: Envoi[] }) {
  const { i18n, t } = useLingui();

  if (envois.length === 0) {
    return (
      <p className="text-xs text-slate-600">
        <Trans>Aucun envoi enregistré pour ce compte rendu. Le PDF ci-dessus est le document validé.</Trans>
      </p>
    );
  }

  // « Médecin adresseur », the word the dossier panel already uses for this person. The API calls the
  // recipient ``medecin_traitant`` and the covering letter says « médecin traitant » — but two labels
  // for one person on two halves of the same screen is how a reader starts wondering whether they are
  // two people.
  const label = (destinataire: string) =>
    destinataire === "patient" ? t`Patient` : destinataire === "medecin_traitant" ? t`Médecin adresseur` : destinataire;

  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-slate-700">
        <Trans>Envoi du compte rendu</Trans>
      </span>
      <ul className="space-y-1.5">
        {envois.map((envoi) => (
          <li key={envoi.destinataire} className="flex items-start gap-1.5 text-xs">
            {envoi.envoye ? (
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            ) : (
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
            )}
            <span className={cn("min-w-0", envoi.envoye ? "text-slate-600" : "text-red-700")}>
              <span className="font-medium text-slate-900">{label(envoi.destinataire)}</span>
              {envoi.envoye ? (
                <Trans> — envoyé à {envoi.adresse_reelle}</Trans>
              ) : (
                <Trans> — non envoyé. {envoi.raison}</Trans>
              )}
              {/* Only when they differ, which is exactly when a reader would otherwise believe the
                  patient received it: under a redirect the message went somewhere else entirely. */}
              {envoi.envoye && envoi.adresse_prevue !== envoi.adresse_reelle && (
                <span className="block text-slate-500">
                  <Trans>Destinataire prévu : {envoi.adresse_prevue}</Trans>
                </span>
              )}
              {/* Only on a copy that left: « pas encore ouvert » under one that never did would read
                  as a second failure where there is one. The count is kept out — a surgeon acts on
                  « lu » or « pas lu », never on « lu deux fois ». */}
              {envoi.envoye && (
                <span className="block text-slate-500">
                  {envoi.ouvert_le ? (
                    <Trans>Ouvert le {formatDateTime(envoi.ouvert_le, i18n.locale) ?? "?"}</Trans>
                  ) : (
                    <Trans>Pas encore ouvert</Trans>
                  )}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReportText({ texte }: { texte: string }) {
  return (
    <div className="space-y-2 px-4 py-4">
      {parseReport(texte).map((block, index) => {
        if (block.kind === "heading") {
          return (
            <h3
              key={index}
              className="pt-2 text-sm font-semibold text-slate-900 first:pt-0"
            >
              {block.text}
            </h3>
          );
        }
        if (block.kind === "bullet") {
          return (
            <p key={index} className="pl-4 text-sm text-slate-700">
              • {block.text}
            </p>
          );
        }
        return (
          <p key={index} className="text-sm leading-relaxed text-slate-700">
            {block.text}
          </p>
        );
      })}
    </div>
  );
}
